import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ShoppingCart, Star, Truck, Shield, ChevronDown, Play, Pause, CheckCircle, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MICRO-TIENDA - Página de producto único
 * 
 * Esta es la página que ve el cliente final.
 * Diseño: Video hero + Producto + Checkout en 2 clicks
 * Filosofía: Premium, minimalista, alta conversión
 */
export default function MicroTienda() {
  const { storeSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadStore();
    // Verificar si volvemos de Stripe
    checkReturnFromStripe();
  }, [storeSlug]);

  // Verificar si regresamos de Stripe con session_id
  const checkReturnFromStripe = async () => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setCheckingPayment(true);
      await pollPaymentStatus(sessionId);
    }
  };

  // Polling para verificar estado del pago
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/cauchera/payments/checkout/status/${sessionId}`);
      const data = await response.json();
      
      if (data.data?.payment_status === 'paid') {
        setPaymentSuccess(true);
        setCheckingPayment(false);
        return;
      } else if (data.data?.status === 'expired') {
        setCheckingPayment(false);
        return;
      }

      // Seguir polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setCheckingPayment(false);
    }
  };

  // Iniciar checkout de Stripe
  const initiateCheckout = async () => {
    setProcessingPayment(true);
    
    try {
      const response = await fetch(`${API_URL}/api/cauchera/payments/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_slug: storeSlug || 'demo',
          product_name: product.name,
          product_price: product.price,
          quantity: quantity,
          origin_url: window.location.origin
        })
      });

      const data = await response.json();
      
      if (data.checkout_url) {
        // Redirigir a Stripe
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Error procesando el pago. Por favor intenta de nuevo.');
      setProcessingPayment(false);
    }
  };

  const loadStore = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cauchera/stores/${storeSlug}`);
      const data = await res.json();
      if (data.success && data.data) {
        // Adaptar datos de la API al formato esperado
        const storeData = data.data;
        const adaptedStore = {
          name: storeData.brand?.brand_name || storeData.name || "Store",
          product: {
            name: storeData.product?.name || "Producto",
            tagline: storeData.brand?.tagline || storeData.product?.tagline || "",
            description: storeData.brand?.description || storeData.product?.description || "",
            price: storeData.product?.price || 39.99,
            originalPrice: storeData.product?.original_price || storeData.product?.price * 1.8,
            currency: storeData.product?.currency || "USD",
            rating: storeData.product?.rating || 4.8,
            reviews: storeData.product?.reviews || 1247,
            video_url: storeData.product?.video_url,
            images: storeData.product?.images || [],
            features: storeData.product?.features || [],
            shipping: storeData.product?.shipping || { free: true, days: "7-14" }
          },
          branding: {
            primaryColor: storeData.brand?.colors?.primary || storeData.branding?.primary_color || "#10B981",
            backgroundColor: storeData.brand?.colors?.background || storeData.branding?.background_color || "#000000"
          },
          logo: storeData.brand?.logo_base64 ? `data:image/png;base64,${storeData.brand.logo_base64}` : null
        };
        setStore(adaptedStore);
      }
    } catch (error) {
      console.error("Error loading store:", error);
    }
    setLoading(false);
  };

  // Demo store para desarrollo
  const demoStore = {
    name: "LumaGrow",
    product: {
      name: "LED Grow Light Pro 1000W",
      tagline: "Cultiva plantas saludables todo el año",
      description: "Luz LED de espectro completo para crecimiento interior. Tecnología Samsung con eficiencia energética superior.",
      price: 39.99,
      originalPrice: 79.99,
      currency: "USD",
      rating: 4.9,
      reviews: 2341,
      video_url: null, // Se llenará con video real
      images: [
        "https://images.unsplash.com/photo-1585336261022-680e295ce3fe?w=800",
        "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800"
      ],
      features: [
        "Espectro completo para todas las etapas",
        "Ahorro 60% vs luces tradicionales",
        "Silencioso - Sin ventilador",
        "Garantía 2 años"
      ],
      shipping: {
        free: true,
        days: "7-12",
        countries: ["US", "UK", "DE", "CA", "AU"]
      }
    },
    branding: {
      primaryColor: "#10B981",
      backgroundColor: "#000000"
    }
  };

  const currentStore = store || demoStore;
  const product = currentStore.product;

  // Modal de verificación de pago
  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-xl text-white">Verificando tu pago...</p>
          <p className="text-zinc-500 mt-2">Por favor espera un momento</p>
        </div>
      </div>
    );
  }

  // Modal de pago exitoso
  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-zinc-900 rounded-2xl p-8 text-center">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">¡Pago Exitoso!</h1>
          <p className="text-zinc-400 mb-6">
            Gracias por tu compra. Recibirás un email con los detalles de tu pedido.
          </p>
          <div className="bg-zinc-800 rounded-xl p-4 mb-6">
            <p className="text-zinc-500 text-sm">Producto</p>
            <p className="text-white font-medium">{product.name}</p>
            <p className="text-emerald-400 font-bold text-lg mt-2">
              ${(product.price * quantity).toFixed(2)} USD
            </p>
          </div>
          <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
            <Truck className="w-4 h-4" />
            <span>Entrega estimada: {product.shipping.days} días</span>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Video Hero Section */}
      <section className="relative h-[70vh] md:h-[80vh] overflow-hidden">
        {product.video_url ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            src={product.video_url}
          />
        ) : (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${product.images[0]})` }}
          />
        )}
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        
        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-4xl">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4">
              {product.name}
            </h1>
            <p className="text-lg md:text-xl text-zinc-300 mb-6">
              {product.tagline}
            </p>
            
            {/* Rating */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'fill-amber-400 text-amber-400' : 'text-zinc-600'}`} 
                  />
                ))}
              </div>
              <span className="text-zinc-400">
                {product.rating} ({product.reviews.toLocaleString()} reviews)
              </span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-4">
              <span className="text-4xl md:text-5xl font-bold text-emerald-400">
                ${product.price}
              </span>
              {product.originalPrice && (
                <span className="text-xl text-zinc-500 line-through">
                  ${product.originalPrice}
                </span>
              )}
              {product.originalPrice && (
                <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium">
                  -{Math.round((1 - product.price / product.originalPrice) * 100)}%
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-4 right-4 md:bottom-8 md:right-8 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>

      {/* Product Details + Buy Section */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Left: Details */}
          <div>
            <h2 className="text-2xl font-bold mb-6">Características</h2>
            <ul className="space-y-4">
              {product.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  </div>
                  <span className="text-zinc-300">{feature}</span>
                </li>
              ))}
            </ul>

            {/* Trust badges */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 bg-zinc-900 rounded-lg">
                <Truck className="w-6 h-6 text-emerald-400" />
                <div>
                  <div className="font-medium">Envío Gratis</div>
                  <div className="text-sm text-zinc-500">{product.shipping.days} días</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-zinc-900 rounded-lg">
                <Shield className="w-6 h-6 text-emerald-400" />
                <div>
                  <div className="font-medium">Garantía</div>
                  <div className="text-sm text-zinc-500">30 días</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Buy Box */}
          <div className="bg-zinc-900 rounded-2xl p-8 h-fit sticky top-8">
            <div className="text-center mb-6">
              <div className="text-3xl font-bold text-emerald-400 mb-2">
                ${product.price} {product.currency}
              </div>
              {product.originalPrice && (
                <div className="text-zinc-500">
                  Ahorra ${(product.originalPrice - product.price).toFixed(2)}
                </div>
              )}
            </div>

            {/* Quantity */}
            <div className="mb-6">
              <label className="block text-sm text-zinc-400 mb-2">Cantidad</label>
              <div className="flex items-center justify-center gap-4">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"
                >
                  -
                </button>
                <span className="text-xl font-medium w-12 text-center">{quantity}</span>
                <button 
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-lg bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"
                >
                  +
                </button>
              </div>
            </div>

            {/* Total */}
            <div className="border-t border-zinc-800 pt-4 mb-6">
              <div className="flex justify-between items-center text-lg">
                <span className="text-zinc-400">Total</span>
                <span className="font-bold text-emerald-400">
                  ${(product.price * quantity).toFixed(2)} {product.currency}
                </span>
              </div>
            </div>

            {/* Buy Button */}
            <button 
              className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-xl text-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={initiateCheckout}
              disabled={processingPayment}
            >
              {processingPayment ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  Comprar Ahora
                </>
              )}
            </button>

            <p className="text-center text-sm text-zinc-500 mt-4">
              Pago seguro con Stripe
            </p>

            {/* Shipping info */}
            <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg text-center">
              <div className="text-sm text-zinc-400">
                <Truck className="w-4 h-4 inline mr-2" />
                Envío gratis • Entrega en {product.shipping.days} días
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="border-t border-zinc-900 py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-amber-400 text-amber-400" />
            ))}
          </div>
          <p className="text-xl text-zinc-300 max-w-2xl mx-auto">
            "Increíble calidad por el precio. Mis plantas nunca han estado mejor."
          </p>
          <p className="text-zinc-500 mt-4">— Sarah M., California</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-sm text-zinc-500">
          <p>© 2025 {currentStore.name}. Todos los derechos reservados.</p>
          <div className="flex justify-center gap-4 mt-4">
            <a href="/terminos" className="hover:text-white">Términos</a>
            <a href="/privacidad" className="hover:text-white">Privacidad</a>
            <a href="/contacto" className="hover:text-white">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
